import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { MetricStatus, MetricType } from 'webapp/enums/enums';

import type { TagRecord } from 'webapp/models';
import type { SourceType } from 'webapp/models-custom';
import Serviced from 'webapp/models-custom/serviced';

interface MetricFromClientModel {
	id: string;
	spaceAccessId?: number | null;
	key: string;
	name: string;
	description: string | null;
	dataSourceTypeKey: string;
	dimensions: DSSetup.DimensionItem[];
	objectType: MetricType;
	sourceAvatar: string;
	sourceName: string;
	isStandalone: boolean;
	hasUnits: boolean;
	status?: MetricStatus;
}

export default class MetricFromClient extends Serviced {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked isDisabled = false;
	@tracked description = '';
	@tracked activeFilter = []; // to trigger change on highlight-result helper when filtering via FilterSort
	@tracked private model: MetricFromClientModel;

	usedCount = 0; // for metric sorting

	constructor(model: MetricFromClientModel) {
		super();

		this.model = model;
		this.description = model.description || '';
	}

	get id(): string {
		return this.model.id;
	}

	get key(): string {
		return this.model.key;
	}

	get name(): string {
		return this.model.name;
	}

	get hasUnits(): boolean {
		return this.model.hasUnits;
	}

	get dimensions(): DSSetup.DimensionItem[] {
		return this.model.dimensions;
	}

	get dataSourceTypeKey(): string {
		return this.model.dataSourceTypeKey;
	}

	get sourceAvatar(): string {
		return this.model.sourceAvatar || '';
	}

	get sourceType(): SourceType | null {
		return this.mmSourceTypesService.findModelByKey(this.model.dataSourceTypeKey);
	}

	get sourceName(): string {
		return this.model.sourceName;
	}

	get sourceTypeName(): string | null {
		return this.sourceType?.name ?? null;
	}

	get objectType(): MetricType {
		return this.model.objectType;
	}

	get spaceAccessId(): number | null {
		return this.model.spaceAccessId ?? null;
	}

	get status(): MetricStatus {
		return this.model.status || MetricStatus.ACTIVE;
	}

	get isTypeBasic(): boolean {
		return this.objectType === MetricType.BASIC;
	}

	get isTypeMetricCalculation(): boolean {
		return this.objectType === MetricType.METRIC_CALCULATION;
	}

	get isTypeCustomQuery(): boolean {
		return this.objectType === MetricType.CUSTOM_QUERY;
	}

	get isTypeGoal(): boolean {
		return this.objectType === MetricType.GOAL;
	}

	get isTypeCustomQueryGeneric(): boolean {
		return this.objectType === MetricType.CUSTOM_QUERY_GENERIC;
	}

	get isTypeCustomQueryOrGeneric(): boolean {
		// any custom query, incl. generic
		return this.isTypeCustomQuery || this.isTypeCustomQueryGeneric;
	}

	get isTypeBuilt(): boolean {
		return this.isTypeCustomQueryOrGeneric || this.isTypeHorizon;
	}

	get isTypeMetricCalculationOrGeneric(): boolean {
		// any script, incl. generic
		return this.isTypeMetricCalculation || this.isTypeMetricCalculationGeneric;
	}

	get isTypeMetricCalculationGeneric(): boolean {
		return this.objectType === MetricType.METRIC_CALCULATION_GENERIC;
	}

	get isTypeHorizon(): boolean {
		return this.objectType === MetricType.HORIZON;
	}

	get isOnSource() {
		return !!this.spaceAccessId;
	}

	get isGenericBasic() {
		// cloud source predefined metrics (only on generic source)
		return this.isTypeBasic && !this.isOnSource;
	}

	get isGenericCustom() {
		// generic script / cq (also from predefined space)
		return this.isTypeCustomQueryGeneric || this.isTypeMetricCalculationGeneric;
	}

	get isGeneric(): boolean {
		return this.isGenericCustom || this.isGenericBasic;
	}

	get isPredefined() {
		// generic public predefined cq / scripts (and its copies on sources)
		return false; // these are not returned for clients
	}

	get isUserGeneric() {
		// generic public predefined cq / scripts (and its copies on sources)
		return false; // these are not returned for clients
	}

	get isPredefinedOnGenericSource() {
		// generic public redefined cq / scripts (only on generic source)
		return false; // these are not returned for clients
	}

	get allowDimensions(): boolean {
		return this.dimensions.length > 0;
	}

	@cached
	get dimensionsString(): string {
		return this.dimensions.map((d) => d.value).join(', ');
	}

	get mustSelectDimension(): boolean {
		return !this.isStandalone && this.allowDimensions;
	}

	get isStandalone() {
		return this.model.isStandalone;
	}

	get autoselectedDimension(): string | null {
		if (!this.isStandalone && this.dimensions.length === 1) {
			return this.dimensions[0]!.key;
		}

		return null;
	}

	get isDeprecated(): boolean {
		return this.model.status === MetricStatus.DISABLED;
	}

	/* --------------------------------------------------- /*
			OTHER STUFF REQUIRED FOR METRIC-SELECT
	/* --------------------------------------------------- */

	@tracked isTemporaryDisabledForMetricSelectPopupYoutube = false;
	@tracked isTemporaryDisabledForMetricSelectPopupNotConnected = false;

	get isTemporaryDisabledForMetricSelectPopup() {
		return (
			this.isTemporaryDisabledForMetricSelectPopupYoutube ||
			this.isTemporaryDisabledForMetricSelectPopupNotConnected
		);
	}

	get metricData(): DSSetup.MetricData | null {
		const key = this.key;

		if (!key) {
			return null;
		}

		return { key, spaceAccessId: this.spaceAccessId };
	}

	get metricSetupData(): DSSetup.MetricSetupData | null {
		// is different from metricData only when autoselectedDimension is available
		const metricData = this.metricData;

		if (metricData) {
			const autoselectedDimension = this.autoselectedDimension;
			if (autoselectedDimension) {
				return Object.assign(
					{
						dimension: autoselectedDimension,
					},
					metricData,
				);
			}
		}
		return metricData;
	}

	get mustResolveBeforeSelecting() {
		return false;
	}

	get canEdit(): boolean {
		return false;
	}

	get source(): null {
		return null;
	}

	get isFavorite(): boolean {
		return false;
	}

	get tagRecords(): TagRecord[] {
		return [];
	}
}
